import Base from './Base';

export default class Profile extends Base {
    get hasProfile() {
        return Boolean(this.businessProfile);
    }

    get hasUseCase() {
        if (!this.useCase) return false;

        return Boolean(Object.keys(this.useCase).length);
    }

    get hasBusinessInformation() {
        return Boolean(this.businessInformation);
    }

    get hasPointsOfContact() {
        return Boolean(
            Array.isArray(this.pointsOfContact) && this.pointsOfContact.length
        );
    }

    get profileTwilioServiceErrors() {
        if (!this.hasProfile) return '';
        if (
            !Array.isArray(this.businessProfile.twilio_service_errors) ||
            (
                Array.isArray(this.businessProfile.twilio_service_errors) &&
                !this.businessProfile.twilio_service_errors.length
            )
        ) return '';

        return this.businessProfile.twilio_service_errors[0].error_message;
    }

    get useCaseTwilioServiceErrors() {
        if (!this.hasUseCase) return '';
        if (
            !Array.isArray(this.useCase.twilio_service_errors) ||
            (
                Array.isArray(this.useCase.twilio_service_errors) &&
                !this.useCase.twilio_service_errors.length
            )
        ) return '';

        return this.useCase.twilio_service_errors[0].error_message;
    }

    get useCaseName() {
        if (!this.hasUseCase) return '';
        if (!this.useCase.use_case) return '';
        if (!this.useCase.use_case.length) return '';

        return this.useCase.use_case[0].name;
    }

    get useCaseId() {
        if (!this.hasUseCase) return '';
        if (!this.useCase.use_case) return '';
        if (!this.useCase.use_case.length) return '';

        return this.useCase.use_case.map(item => String(item.id));
    }

    get hasCreated10DLC() {
        if (!this.hasUseCase) return false;

        return Boolean(this.useCase.created_at);
    }

    // businessProfile
    get profileName() {
        if (!this.hasProfile) return '—';

        return this.businessProfile.name;
    }

    get profileCountNumbers() {
        if (!this.hasProfile) return 0;

        return this.businessProfile.count_numbers || 3;
    }

    get businessName() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.business_name;
    }

    get brandType() {
        if (!this.hasBusinessInformation) return '—';
        if (this.hasPlanEmpty) return '—';
        if (!this.hasCreated10DLC) return '—';

        return this.businessInformation.brand_type || '—';
    }

    get dlcCurrentPlanTypeId() {
        if (!this.hasProfile) return 1;

        return this.businessProfile.currentPlanTypeId;
    }

    get dlcPlanTypeId() {
        if (!this.hasProfile) return 1;

        return this.businessProfile.planTypeId;
    }

    get hasPlanEmpty() {
        return this.dlcPlanTypeId === 1;
    }

    get hasPlanStandard() {
        return this.dlcPlanTypeId === 2;
    }

    get hasPlanLowVolume() {
        return this.dlcPlanTypeId === 3;
    }

    get trustScore() {
        if (!this.hasBusinessInformation) return '—';
        if (this.hasPlanEmpty) return '—';
        if (!this.hasCreated10DLC) return '—';

        return this.businessInformation.brandScore || '—';
    }

    get registrationNumber() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.registration_number;
    }

    get socialMediaUrl() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.social_media_url;
    }

    get businessTypeName() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.business_type.name;
    }

    get industryName() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.industry.name;
    }

    get websiteUrl() {
        if (!this.hasBusinessInformation) return '—';

        return this.businessInformation.website_url;
    }

    get regionsOfOperation() {
        if (!this.hasBusinessInformation) return [];

        return this.businessInformation.regions_of_operation;
    }

    get address() {
        if (!this.hasBusinessInformation) return '—';

        const { city, street_address_1, street_address_2, state_id, zip_code } = this.businessInformation;
        const streetAddress = street_address_1 || street_address_2;

        return `${city} ${streetAddress} ${state_id} ${zip_code}`;
    }

    get pointsOfContacts() {
        if (!this.hasPointsOfContact) return [];

        return this.pointsOfContact.map(item => {
            return {
                ...item,
                full_name: `${item.first_name} ${item.last_name}`
            };
        });
    }

    // status profile
    get regProgressStatus() {
        if (!this.hasProfile) return 'empty';

        return this.businessProfile.regProgressStatus;
    }

    get hasFormStepOneRegProgressStatus() {
        return this.regProgressStatus === 'form_step_1';
    }

    get hasPlanSelectedRegProgressStatus() {
        return this.regProgressStatus === 'plan_selected';
    }

    // status profile
    get status() {
        if (!this.hasProfile) return 'empty';

        return this.businessProfile.status;
    }

    get statusLabel() {
        if (this.type === '10dlc') {
            if (this.hasPlanEmpty) return '—';
            if (!this.hasCreated10DLC) return '—';
        }

        if (
            this.type === 'shaken_stir' &&
            this.hasStatusEmpty
        ) {
            return 'Approved';
        }

        if (this.hasStatusUnderReview) {
            return 'Under Review';
        }

        if (this.hasStatusFailed) {
            return 'Rejected';
        }

        if (this.hasStatusApproved) {
            return 'Approved';
        }

        if (this.hasStatusPending) {
            return 'Pending';
        }

        if (this.hasStatusPendingProfileApprovalUseCase) {
            return 'Pending Business Profile Approval';
        }

        if (this.hasStatusInReview) {
            return 'In Review';
        }

        if (this.hasStatusEmpty) {
            return '—';
        }
    }

    get hasStatusEmpty() {
        return this.status === 'empty';
    }

    get hasEmptyFields() {
        return !this.hasCompanyProfile;
    }

    get hasStatusApproved() {
        return this.status === 'approved';
    }

    get hasStatusFailed() {
        return this.status === 'failed';
    }

    get hasStatusPending() {
        return this.status === 'pending';
    }

    get hasStatusInReview() {
        return this.status === 'in_review';
    }

    get hasStatusUnderReview() {
        return this.status === 'under_review';
    }

    get hasStatusPendingProfileApproval() {
        return this.status === 'pending_business_profile_approval';
    }

    get statusStyles() {
        if (
            this.type === 'shaken_stir' &&
            this.hasStatusEmpty
        ) {
            return {
                color: '#13cb6e',
            };
        }

        if (this.hasStatusEmpty) {
            return {
                color: '#E6B05F',
            };
        }

        if (this.hasStatusFailed) {
            return {
                color: '#da5350',
            };
        }

        if (this.hasStatusPending || this.hasStatusInReview || this.hasStatusPendingProfileApproval) {
            return {
                color: '#7a7a7a',
            };
        }

        return {
            color: '#13cb6e',
        };
    }

    get statusIcon() {
        if (
            this.type === 'shaken_stir' &&
            this.hasStatusEmpty
        ) {
            return ['fas', 'check-circle'];
        }

        if (this.hasStatusEmpty) {
            return ['fas', 'exclamation-triangle'];
        }

        if (this.hasStatusFailed) {
            return ['fas', 'times-circle'];
        }

        if (
            this.hasStatusUnderReview ||
            this.hasStatusPending ||
            this.hasStatusPendingProfileApproval ||
            this.hasStatusInReview ||
            this.hasStatusEmpty
        ) {
            return ['fas', 'clock'];
        }

        return ['fas', 'check-circle'];
    }

    // status use case
    get statusUseCase() {
        if (!this.hasUseCase) return 'empty';
        if (!this.useCase.status) return 'empty';

        return this.useCase.status;
    }

    get statusUseCaseLabel() {
        if (this.hasStatusUseCaseInProgress) {
            return 'In Progress';
        }

        if (this.hasStatusPendingProfileApprovalUseCase) {
            return 'Pending Business Profile Approval';
        }

        if (this.hasStatusUseCaseUnderReview) {
            return 'Under Review';
        }

        if (this.hasStatusUseCaseFailed) {
            return 'Rejected';
        }

        if (this.hasStatusUseCaseApproved) {
            return 'Approved';
        }

        if (this.hasStatusUseCaseInReview) {
            return 'In Review';
        }

        if (this.hasStatusUseCaseEmpty) {
            return '—';
        }
    }

    get hasStatusUseCaseEmpty() {
        return this.statusUseCase === 'empty';
    }

    get hasStatusUseCaseApproved() {
        return this.statusUseCase === 'approved';
    }

    get hasStatusPendingProfileApprovalUseCase() {
        return this.statusUseCase === 'pending_business_profile_approval';
    }

    get hasStatusUseCaseFailed() {
        return this.statusUseCase === 'failed';
    }

    get hasStatusUseCaseInReview() {
        return this.statusUseCase === 'in_review';
    }

    get hasStatusUseCaseInProgress() {
        return this.statusUseCase === 'in_progress';
    }

    get hasStatusUseCaseUnderReview() {
        return this.statusUseCase === 'under_review';
    }

    get statusUseCaseStyles() {
        if (
            this.type === 'shaken_stir' &&
            this.hasStatusUseCaseEmpty
        ) {
            return {
                color: '#13cb6e',
            };
        }

        if (this.hasStatusUseCaseEmpty) {
            return {
                color: '#E6B05F',
            };
        }

        if (this.hasStatusUseCaseFailed) {
            return {
                color: '#da5350',
            };
        }

        if (
            this.hasStatusUseCaseUnderReview ||
            this.hasStatusUseCaseInReview ||
            this.hasStatusPendingProfileApproval
        ) {
            return {
                color: '#7a7a7a',
            };
        }

        return {
            color: '#13cb6e',
        };
    }

    get statusUseCaseIcon() {
        if (this.hasStatusUseCaseEmpty) {
            return ['fas', 'exclamation-triangle'];
        }

        if (this.hasStatusUseCaseFailed) {
            return ['fas', 'times-circle'];
        }

        if (
            this.hasStatusUseCaseInProgress ||
            this.hasStatusUseCaseUnderReview ||
            this.hasStatusUseCaseInReview ||
            this.hasStatusUseCaseEmpty
        ) {
            return ['fas', 'clock'];
        }

        return ['fas', 'check-circle'];
    }

    get hasCreateShakenStir() {
        return Boolean(
            !this.hasStatusEmpty &&
            this.hasTerms
        );
    }

    get hasTerms() {
        return Boolean(
            this.businessProfile.terms
        );
    }

    // Attestation
    get statusAttestationLabel() {
        return 'Full';
    }

    get statusAttestationStyles() {
        return {
            color: '#13cb6e',
        };
    }

    get statusAttestationIcon() {
        return ['fas', 'check-circle'];
    }

    get showButtonsCNAM() {
        if (!this.hasStatusPending) {
            return true;
        }

        return false;
    }

    get hasCompanyProfile() {
        if (!this.hasBusinessInformation) return false;

        return Boolean(
            this.businessInformation.business_name &&
            this.businessInformation.first_name &&
            this.businessInformation.last_name &&
            this.businessInformation.phone_number &&
            this.businessInformation.registration_number &&
            this.businessInformation.website_url &&
            this.businessInformation.street_address_1 &&
            this.businessInformation.country_id &&
            this.businessInformation.city
        );
    }

    get hasBusinessProfileStepOne() {
        if (!this.hasBusinessInformation) return false;

        return Boolean(
            this.businessInformation.business_name &&
            this.businessInformation.city &&
            this.businessInformation.registration_number &&
            this.businessInformation.registration_type_id &&
            this.businessInformation.state_id &&
            this.businessInformation.street_address_1 &&
            this.businessInformation.zip_code
        );
    }

    get hasBusinessProfileStepTwo() {
        if (!this.hasBusinessInformation) return false;

        return Boolean(
            this.businessInformation.business_type_id &&
            this.businessInformation.industry_id &&
            this.businessInformation.regions_of_operation.length &&
            this.businessInformation.website_url
        );
    }

    get hasBusinessProfileStepThree() {
        return Boolean(
            this.hasPointsOfContact
        );
    }

    get hasBusinessProfileStepFour() {
        return Boolean(
            this.terms
        );
    }

    get hasBusinessProfile() {
        return Boolean(
            this.hasBusinessProfileStepOne &&
            this.hasBusinessProfileStepTwo &&
            this.hasBusinessProfileStepThree &&
            this.hasBusinessProfileStepFour &&
            this.hasProfile
        );
    }

    get hasBusinessProfileV2() {
        return Boolean(
            (this.businessInformation?.registration_type_id === 3 &&
            this.businessInformation?.reg_progress_status === 'data_collected') || this.businessProfile !== null
        );
    }

    get isSoleProfile() {
        return Boolean(this.businessInformation?.registration_type_id === 3);
    }

    get hasNotCompleteBusinessProfile() {
        return Boolean(
            this.hasBusinessProfileStepOne ||
            this.hasBusinessProfileStepTwo ||
            this.hasBusinessProfileStepThree ||
            this.hasBusinessProfileStepFour
        );
    }
}
